import React, { useState, useEffect } from 'react'
import Sidebar from '../../mini-comp/Sidebar'
import Topbar from '../../mini-comp/Topbar'
import axios from 'axios'
import Cookies from 'js-cookie'

const Professionals = () => {
    const baseUrl = "https://api.recruit.burrayhr.com";
    const [jobSeekers, setJobSeekers] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchError, setFetchError] = useState('');
    const [professionFilter, setProfessionFilter] = useState('');
    const [experienceInput, setExperienceInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        let isCancelled = false;
        const controller = new AbortController();

        const fetchData = async () => {
            setIsLoading(true);
            setFetchError('');
            try {
                // map filters to query params (server-side)
                const params = {
                    profession: professionFilter || undefined,
                    experience: experienceInput || undefined,
                    q: searchQuery || undefined,
                };
                const response = await axios.get(`${baseUrl}/api/job-seekers/`, {
                    headers: {
                        'Authorization': `Bearer ${Cookies.get('token')}`
                    },
                    params,
                    signal: controller.signal,
                });
                if (!isCancelled) {
                    setJobSeekers(response.data);
                    console.log(response.data);
                }
            } catch (err) {
                console.log(err);
                if (!isCancelled) {
                    if (axios.isCancel?.(err)) return;
                    setFetchError(err?.response?.data?.detail || err?.message || 'Failed to load job seekers');
                }
            } finally {
                if (!isCancelled) setIsLoading(false);
            }
        };

        // debounce search to avoid excessive requests
        const handle = setTimeout(fetchData, 350);
        return () => {
            isCancelled = true;
            controller.abort();
            clearTimeout(handle);
        };
    }, [professionFilter, experienceInput, searchQuery]);

    // Derive counts from response shape (list or aggregate object)
    const jobSeekerList = Array.isArray(jobSeekers)
        ? jobSeekers
        : (jobSeekers?.results || []);

    // Build profession options from data
    const professionOptions = Array.from(new Set(
        jobSeekerList
            .map(js => js?.profession || js?.job_title || js?.role || '')
            .filter(Boolean)
    ));

    return (
        <div>
            <Topbar />
            <Sidebar />

            <div className="app-content-area">
                <div className="app-content-wrap">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-xl-12">
                                <div className="card mb-4">
                                    <div className="card-body">
                                        <div className="row g-3 align-items-end">
                                            <div className="col-md-3">
                                                <label className="form-label">Profession</label>
                                                <select
                                                    className="form-select"
                                                    value={professionFilter}
                                                    onChange={(e) => setProfessionFilter(e.target.value)}
                                                >
                                                    <option value="">Any</option>
                                                    {professionOptions.map(opt => (
                                                        <option key={opt} value={opt}>{opt}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="col-md-3">
                                                <label className="form-label">Experience (Years)</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    min="0"
                                                    step="1"
                                                    value={experienceInput}
                                                    onChange={e => setExperienceInput(e.target.value)}
                                                    placeholder="e.g. 3"
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">Search</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    placeholder="Skills, qualifications, etc."
                                                />
                                            </div>

                                            <div className="col-md-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-light w-100"
                                                    onClick={() => { setProfessionFilter(''); setExperienceInput(''); setSearchQuery(''); }}
                                                >
                                                    Clear Filters
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {isLoading && (
                                    <div className="text-center py-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="text-muted mt-2">Loading professionals...</p>
                                    </div>
                                )}

                                {!!fetchError && (
                                    <div className="alert alert-danger" role="alert">
                                        {fetchError}
                                    </div>
                                )}

                                <div className="row g-3">
                                    {jobSeekerList.map((js) => {
                                        const key = js?.id || js?.user_id || js?.email || Math.random().toString(36);
                                        const fullName = js?.full_name || [js?.first_name, js?.last_name].filter(Boolean).join(' ') || 'Unnamed';
                                        const email = js?.user_data?.email || 'N/A';
                                        const profession = js?.profession || js?.job_title || js?.role || '—';
                                        const status = (js?.unemployed === true || js?.is_employed === false || js?.employment_status === 'unemployed' || js?.status === 'unemployed') ? 'Unemployed' : 'Employed';
                                        const rawImage = js?.profile_picture;
                                        const imageUrl = rawImage ? (String(rawImage).startsWith('http') ? rawImage : `${baseUrl}${rawImage}`) : null;
                                        const initials = fullName
                                            .split(' ')
                                            .filter(Boolean)
                                            .slice(0, 2)
                                            .map(s => s[0]?.toUpperCase())
                                            .join('');

                                        return (
                                            <div key={key} className="col-md-3 col-xxl-3">
                                                <div
                                                    className="card h-100 cursor-pointer hover-shadow"
                                                    onClick={() => {
                                                        if (js?.id) {
                                                            window.location.href = `/hr/recruitment/professional/${js.id}`;
                                                        }
                                                    }}
                                                >
                                                    <div className="card-body">
                                                        <div className="d-flex align-items-center mb-3">
                                                            <div className="avatar avatar-lg me-3 flex-shrink-0">
                                                                {imageUrl ? (
                                                                    <img src={imageUrl} alt={fullName} className="rounded-circle" />
                                                                ) : (
                                                                    <div className="avatar-title bg-primary-subtle text-primary rounded-circle fw-bold fs-5">
                                                                        {initials || '?'}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="flex-grow-1 overflow-hidden">
                                                                <h6 className="mb-1 text-truncate" title={fullName}>{fullName}</h6>
                                                                <span className={`badge ${status === 'Unemployed' ? 'bg-warning-subtle text-warning' : 'bg-success-subtle text-success'}`}>
                                                                    {status}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <p className="text-muted small mb-3 text-truncate" title={email}>{email}</p>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            <span className="badge bg-light text-dark border fw-normal">
                                                                {profession}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {!isLoading && jobSeekerList.length === 0 && (
                                        <div className="col-12 text-center py-5">
                                            <div className="text-muted">No job seekers found matching your criteria.</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Professionals
